import {
  illustrationPendingDotClassName,
  piiSpanClassName,
  snippetTextClassName,
} from '@/pages/pii-training/piiTraining.styles';
import type {
  IllustrationCardProps,
  PiiIllustrationProps,
  QuizIllustrationProps,
  QuizResultRowProps,
  RecapIllustrationProps,
  RecapRowProps,
  SnippetIllustrationProps,
} from '@/pages/pii-training/piiTraining.types';
import { Box, Icon, Text } from '@careflux/primitives';

const IllustrationCard = ({ children }: IllustrationCardProps) => (
  <Box display="flex" direction="col" gap="md" background="card" shadow="2xl" rounded="xl" padding="lg" width="80">
    {children}
  </Box>
);

/** Steps 1 and 2: a dictation snippet with the identifying details marked. */
const SnippetIllustration = ({ copy }: SnippetIllustrationProps) => (
  <IllustrationCard>
    <Box display="flex" align="center" gap="xs">
      <Icon name="Mic" size="xs" color="muted" />
      <Text size="xs" color="muted" weight="medium">
        {copy.heading}
      </Text>
    </Box>
    <Text asChild className={snippetTextClassName}>
      <p>
        {copy.snippet.map((span, index) =>
          span.pii ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: static copy, spans never reorder
            <mark key={index} className={piiSpanClassName}>
              {span.text}
            </mark>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: static copy, spans never reorder
            <span key={index}>{span.text}</span>
          )
        )}
      </p>
    </Text>
    <Text size="xs" color="muted">
      {copy.footer}
    </Text>
  </IllustrationCard>
);

const QuizResultRow = ({ question, index, answer, copy }: QuizResultRowProps) => {
  const isAnswered = answer !== null;
  const isCorrect = answer === question.correctAnswer;
  const verdict = question.correctAnswer === 'yes' ? copy.illustration.identifiable : copy.illustration.notIdentifiable;

  return (
    <Box display="flex" align="start" gap="sm" rounded="lg" border="default" padding="sm">
      {!isAnswered && (
        <Box width="5" height="5" flexShrink="0" rounded="full" className={illustrationPendingDotClassName} />
      )}
      {isAnswered && (
        <Icon name={isCorrect ? 'CircleCheck' : 'CircleX'} color={isCorrect ? 'success' : 'destructive'} />
      )}
      <Box display="flex" direction="col" gap="xs">
        <Text size="sm" weight="semibold">
          {copy.exampleLabel(index + 1)}
          {isAnswered && ` · ${isCorrect ? copy.illustration.correct : copy.illustration.incorrect}`}
        </Text>
        <Text size="xs" color="muted">
          {isAnswered ? `${question.keyDetail} → ${verdict}` : copy.illustration.unanswered}
        </Text>
      </Box>
    </Box>
  );
};

/** Step 3: the three examples, revealing the deciding detail once answered. */
const QuizIllustration = ({ copy, values }: QuizIllustrationProps) => (
  <IllustrationCard>
    <Text size="sm" weight="semibold">
      {copy.illustration.heading}
    </Text>
    {copy.questions.map((question, index) => (
      <QuizResultRow key={question.id} question={question} index={index} answer={values[question.id]} copy={copy} />
    ))}
  </IllustrationCard>
);

const RecapRow = ({ label, done }: RecapRowProps) => (
  <Box display="flex" align="center" gap="sm">
    {done ? (
      <Icon name="CircleCheck" color="success" />
    ) : (
      <Box width="5" height="5" flexShrink="0" rounded="full" className={illustrationPendingDotClassName} />
    )}
    <Text size="sm" color={done ? 'default' : 'muted'}>
      {label}
    </Text>
  </Box>
);

/** Step 4: what the training covered, ticking off as the reader finishes. */
const RecapIllustration = ({ copy, values }: RecapIllustrationProps) => {
  const { items } = copy.consent.illustration;
  const quizDone = copy.quiz.questions.every((question) => values[question.id] !== null);

  return (
    <IllustrationCard>
      <Box display="flex" align="center" gap="sm">
        <Icon name="ShieldCheck" size="sm" color="primary" />
        <Text size="sm" weight="semibold">
          {copy.consent.illustration.heading}
        </Text>
      </Box>
      <RecapRow label={items.obvious} done />
      <RecapRow label={items.combinations} done />
      <RecapRow label={items.quiz} done={quizDone} />
      <RecapRow label={items.consent} done={values.consented} />
    </IllustrationCard>
  );
};

export const PiiIllustration = ({ step, copy, values }: PiiIllustrationProps) => {
  switch (step) {
    case 2:
      return <SnippetIllustration copy={copy.combinations.illustration} />;
    case 3:
      return <QuizIllustration copy={copy.quiz} values={values} />;
    case 4:
      return <RecapIllustration copy={copy} values={values} />;
    default:
      return <SnippetIllustration copy={copy.whatIsPii.illustration} />;
  }
};
